// 👥 Employés E3b — Rôles multiples (chips) + Rôle principal, section Emploi de la fiche.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ROLES_ATTRIBUABLES, libelleRole, rolesDe } from '@/lib/roles';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

export function RolesMultiples({ employee, isProprietaire, onSaved }) {
  const [roles, setRoles] = useState(rolesDe(employee));
  const [principal, setPrincipal] = useState(employee?.role_principal || rolesDe(employee)[0] || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRoles(rolesDe(employee)); setPrincipal(employee?.role_principal || rolesDe(employee)[0] || ''); }, [employee?.id, employee?.roles, employee?.role_principal]); // eslint-disable-line react-hooks/exhaustive-deps

  const basculer = (r) => {
    const next = roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r];
    setRoles(next);
    if (!next.includes(principal)) setPrincipal(next[0] || '');
  };
  const dirty = JSON.stringify(roles) !== JSON.stringify(rolesDe(employee)) || principal !== (employee?.role_principal || rolesDe(employee)[0] || '');

  const enregistrer = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/employees/${employee.id}`, { roles, role_principal: principal || null }, { headers: headers() });
      toast.success('Rôles enregistrés');
      onSaved?.(data.employee || { ...employee, roles, role_principal: principal });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement des rôles");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="roles-multiples">
      <div>
        <Label className="text-slate-600">Rôles</Label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {ROLES_ATTRIBUABLES.map((r) => {
            const actif = roles.includes(r);
            return (
              <button
                key={r} type="button" disabled={!isProprietaire} onClick={() => basculer(r)} data-testid={`chip-role-${r}`}
                aria-pressed={actif}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${actif ? 'bg-[#1a237e] text-white border-[#1a237e]' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'} ${!isProprietaire ? 'cursor-default opacity-80' : ''}`}
              >
                {libelleRole(r)}
              </button>
            );
          })}
        </div>
        {roles.length === 0 && <p className="text-xs text-red-600 mt-1" data-testid="roles-vide">Un employé actif doit avoir au moins un rôle.</p>}
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <div className="min-w-[220px]">
          <Label className="text-slate-600">Rôle principal</Label>
          {isProprietaire ? (
            <Select value={principal || undefined} onValueChange={setPrincipal} disabled={roles.length === 0}>
              <SelectTrigger className="mt-1.5" data-testid="select-role-principal"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{libelleRole(r)}</SelectItem>)}</SelectContent>
            </Select>
          ) : <div className="mt-1.5"><Badge variant="outline" data-testid="badge-role-principal">{libelleRole(principal)}</Badge></div>}
        </div>
        {isProprietaire && (
          <Button size="sm" disabled={!dirty || saving || roles.length === 0 || !principal} onClick={enregistrer} data-testid="btn-enregistrer-roles">Enregistrer les rôles</Button>
        )}
      </div>
      <p className="text-xs text-slate-500">Le compte lié reçoit l'union des accès de tous les rôles ; le rôle principal sert d'affichage et de menu par défaut.</p>
    </div>
  );
}
